import React, {useState, useEffect, useMemo, useRef, useCallback} from 'react';
import {
    useMaterialReactTable,
    MaterialReactTable,
} from 'material-react-table';
import {Box, Typography, Chip, Link, Button, IconButton, Avatar, CircularProgress, Tooltip} from '@mui/material';
import {Link as RouterLink} from 'react-router-dom';
import apiService from '../../services/apiService';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import DeleteIcon from "@mui/icons-material/Delete";
import ConfirmDialog from "../../components/ConfirmDialog";
import Protected from "../../components/Protected";
import {ROLES} from "../../config/roles";

const PrizeItemsPage = () => {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isUploading, setIsUploading] = useState(false);
    const fileInputRef = useRef(null);

    const [confirmOpen, setConfirmOpen] = useState(false);
    const [itemToDelete, setItemToDelete] = useState(null);
    const [isDeletingAll, setIsDeletingAll] = useState(false);
    const [rowSelection, setRowSelection] = useState({});

    const fetchItems = useCallback(() => {
        setLoading(true);
        apiService.get('/api/admin/prizes/items')
            .then(data => setItems(data))
            .catch(err => setError(err.message || 'Failed to fetch prize items'))
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => {
        fetchItems();
    }, [fetchItems]);

    const handleDeleteItem = async () => {
        try {
            await apiService.delete(`/api/admin/prizes/items/${itemToDelete.id}`);
            fetchItems(); // Refresh the list
        } catch (err) {
            setError('Failed to delete item.');
        } finally {
            setConfirmOpen(false);
            setItemToDelete(null);
        }
    };

    const handleDeleteAll = async () => {
        try {
            await apiService.delete('/api/admin/prizes/items/all');
            fetchItems(); // Refresh the list
        } catch (err) {
            setError('Failed to delete all items.');
        } finally {
            setConfirmOpen(false);
            setIsDeletingAll(false);
        }
    };

    const handleBulkDelete = async () => {
        try {
            const selectedIndices = Object.keys(rowSelection).map(Number);
            const idsToDelete = selectedIndices.map(index => items[index]?.id).filter(Boolean);
            await apiService.post('/api/admin/prizes/items/bulk-delete', idsToDelete);
            fetchItems();
            setRowSelection({});
        } catch (err) {
            setError('Failed to delete selected items.');
        } finally {
            setConfirmOpen(false);
            setIsDeletingAll(false);
        }
    };

    const columns = useMemo(
        () => [
            {
                accessorKey: 'id',
                header: 'ID',
                size: 50,
            },
            {
                accessorKey: 'imageUrl',
                header: 'Image',
                size: 60,
                Cell: ({cell, row}) => (
                    <Avatar src={cell.getValue()} alt={row.original.name}/>
                ),
            },
            {
                accessorKey: 'name',
                header: 'Name',
                Cell: ({cell, row}) => (
                    <Link component={RouterLink} to={`/prize-items/${row.original.id}`}>
                        {cell.getValue()}
                    </Link>
                ),
            },
            {
                accessorKey: 'categoryName',
                header: 'Category',
                Cell: ({cell, row}) => (
                    <Link component={RouterLink} to={`/prize-categories/${row.original.categoryId}`}>
                        {cell.getValue()}
                    </Link>
                ),
            },
            {
                accessorKey: 'stock',
                header: 'Stock',
                size: 100,
            },
            {
                accessorKey: 'active',
                header: 'Status',
                size: 100,
                Cell: ({cell}) => (
                    <Chip
                        label={cell.getValue() ? 'Active' : 'Inactive'}
                        color={cell.getValue() ? 'success' : 'default'}
                        size="small"
                    />
                ),
            },
        ],
        []
    );

    const handleDownload = () => {
        window.open('/api/admin/prizes/csv/download', '_blank');
    };

    const handleFileUpload = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        setIsUploading(true);
        try {
            const formData = new FormData();
            formData.append('file', file);

            await apiService.post('/api/admin/prizes/csv/upload', formData);

            alert('CSV uploaded successfully! Refreshing data...');
            fetchItems();
        } catch (err) {
            alert('Failed to upload CSV. Please check the console for details.');
            console.error(err);
        } finally {
            setIsUploading(false);
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    };

    const table = useMaterialReactTable({
        columns,
        data: items,
        state: {isLoading: loading, showAlertBanner: !!error, rowSelection,},
        muiToolbarAlertBannerProps: error ? {color: 'error', children: `Error: ${error}`} : undefined,
        initialState: {
            sorting: [{id: 'name', desc: false}],
        },
        renderTopToolbarCustomActions: () => {
            const selectedCount = Object.keys(rowSelection).length;
            return (
                <Box sx={{display: 'flex', gap: '1rem', flexWrap: 'wrap'}}>
                    <Protected component={PrizeItemsPage} action="create">
                        <Button
                            component={RouterLink}
                            to="/prize-items/new"
                            variant="contained"
                            startIcon={<AddIcon/>}
                        >
                            Create Item
                        </Button>
                    </Protected>
                    {selectedCount > 0 ? (
                        <Protected component={PrizeItemsPage} action="delete">
                            <Button
                                color="error"
                                variant="contained"
                                startIcon={<DeleteIcon/>}
                                onClick={() => {
                                    setIsDeletingAll(true);
                                    setConfirmOpen(true);
                                }}
                            >
                                Delete ({selectedCount}) Selected
                            </Button>
                        </Protected>
                    ) : (
                        <Protected component={PrizeItemsPage} action="manageCsv">
                            <>
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept=".csv"
                                    onChange={handleFileUpload}
                                    style={{display: 'none'}}
                                />
                                <Button
                                    variant="outlined"
                                    startIcon={isUploading ? <CircularProgress size={20}/> : <FileUploadIcon/>}
                                    onClick={() => fileInputRef.current.click()}
                                    disabled={isUploading}
                                >
                                    {isUploading ? 'Uploading...' : 'Upload CSV'}
                                </Button>
                                <Button
                                    variant="outlined"
                                    startIcon={<FileDownloadIcon/>}
                                    onClick={handleDownload}
                                >
                                    Download CSV
                                </Button>
                            </>
                        </Protected>
                    )}
                </Box>
            )
        },
        enableRowSelection: true,
        onRowSelectionChange: setRowSelection,
        enableRowActions: true,
        displayColumnDefOptions: {
            'mrt-row-select': {header: '', size: 20,},
            'mrt-row-actions': {header: 'Actions', size: 100,}
        },
        positionActionsColumn: 'last',
        positionGlobalFilter: 'right',
        renderRowActions: ({row}) => (
            <Box sx={{display: 'flex', gap: '0.5rem'}}>
                <Tooltip title="Edit">
                    <IconButton component={RouterLink} to={`/prize-items/${row.original.id}`}>
                        <EditIcon/>
                    </IconButton>
                </Tooltip>
                <Protected component={PrizeItemsPage} action="delete">
                    <Tooltip title="Delete">
                        <IconButton
                            color="error"
                            onClick={() => {
                                setItemToDelete(row.original);
                                setConfirmOpen(true);
                            }}
                        >
                            <DeleteIcon/>
                        </IconButton>
                    </Tooltip>
                </Protected>
            </Box>
        ),
    });

    return (
        <Box sx={{p: 2}}>
            <Typography variant="h4" gutterBottom>
                Prize Items
            </Typography>
            <MaterialReactTable table={table}/>

            {/* Confirmation Dialog */}
            <Protected component={PrizeItemsPage} action="delete">
                <ConfirmDialog
                    open={confirmOpen}
                    onClose={() => {
                        setConfirmOpen(false);
                        setItemToDelete(null);
                        setIsDeletingAll(false);
                    }}
                    onConfirm={isDeletingAll ? handleBulkDelete : handleDeleteItem}
                    title={isDeletingAll ? "Delete Selected Items?" : `Delete Item: ${itemToDelete?.name}?`}
                    message={
                        isDeletingAll
                            ? `Are you sure you want to delete the ${Object.keys(rowSelection).length} selected items? This action cannot be undone.`
                            : "Are you sure you want to delete this prize item? This action cannot be undone."
                    }
                />
            </Protected>
        </Box>
    );
};

PrizeItemsPage.abilities = {
    read: [ROLES.VIEWERS, ROLES.SUPPORT, ROLES.ADMINS],
    create: [ROLES.ADMINS],
    update: [ROLES.ADMINS],
    delete: [ROLES.ADMINS],
    manageCsv: [ROLES.ADMINS],
};

export default PrizeItemsPage;